import { postMessageChiffre, postSignature, postKey } from './relay';
import type { RelayConfig } from '../types/identity';
import type { MsgChiffre, MsgSignature, MsgCle } from '../types/message';
import type { ProofSignature } from './collectSignatures';

export interface RelayPublishStatus {
  endpoint: string;
  ok: boolean;
  error?: string;
}

export interface PublishResult {
  successCount: number;
  relayStatus: RelayPublishStatus[];
}

/**
 * Publish message + optional MsgCle + ourSigs to relays (best effort per relay).
 * When msgCle is provided (e.g. login ECDH), posts it for scan → fetch → decrypt flow.
 * Returns detailed status per relay.
 */
export async function publishMessageAndSigs(
  relays: RelayConfig[],
  msgChiffre: MsgChiffre,
  ourSigs: ProofSignature[],
  msgCle?: MsgCle | null,
): Promise<PublishResult> {
  const relayStatus: RelayPublishStatus[] = [];
  let successCount = 0;

  for (const r of relays) {
    if (!r.enabled) {
      continue;
    }
    try {
      // Ordre strict : message → signatures → clés
      await postMessageChiffre(r.endpoint, msgChiffre);
      if (msgCle !== undefined && msgCle !== null) {
        await postKey(r.endpoint, msgCle);
      }
      for (const sig of ourSigs) {
        const msgSig: MsgSignature = {
          signature: {
            hash: msgChiffre.hash,
            cle_publique: sig.cle_publique,
            signature: sig.signature,
            nonce: sig.nonce,
          },
          hash_cible: msgChiffre.hash,
        };
        await postSignature(r.endpoint, msgSig);
      }
      relayStatus.push({ endpoint: r.endpoint, ok: true });
      successCount++;
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : String(err);
      console.error(`Publish to ${r.endpoint} failed:`, err);
      relayStatus.push({
        endpoint: r.endpoint,
        ok: false,
        error: errorMsg,
      });
    }
  }

  return { successCount, relayStatus };
}
